import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Bell,
  Warning,
  X,
  ShieldWarning,
  PencilSimple,
  UploadSimple,
  CheckCircle,
} from "@phosphor-icons/react";
import { devDebug } from "@/lib/debug";

// Poll every 60s.
const POLL_MS = 60_000;
// Per-browser read-stamp so the badge zeros once user opens the bell.
const READ_STAMP_KEY = "epool.notifications.readAt";

const ICONS = {
  mismatch: Warning,
  fraud: ShieldWarning,
  correction: PencilSimple,
  upload: UploadSimple,
};

const TONES = {
  danger: { icon: "text-red-600", bg: "bg-red-100" },
  warning: { icon: "text-amber-700", bg: "bg-amber-100" },
  info: { icon: "text-blue-700", bg: "bg-blue-100" },
};

/**
 * NotificationBell — polls `/api/notifications/summary` every 60s and
 * renders a badge + dropdown covering all notification kinds (mismatch,
 * fraud attempts, pending corrections, latest upload). Read state is
 * per-browser via localStorage.
 *
 * Displayed on desktop sidebar AND mobile drawer (via AppLayout).
 */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [readAt, setReadAt] = useState(() => localStorage.getItem(READ_STAMP_KEY) || "");
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/summary");
      setItems(data.items || []);
      setTotal(Number(data.unread_total) || 0);
    } catch (err) {
      // Silent — bell is nice-to-have.
      devDebug("notif summary load failed", err?.message);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markRead = () => {
    const now = new Date().toISOString();
    localStorage.setItem(READ_STAMP_KEY, now);
    setReadAt(now);
  };

  const onToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) markRead();
  };

  // Badge zeroes as soon as user opens the bell — until fresh events come
  // in on the next poll (server total > local readAt threshold in items).
  const badge = readAt ? 0 : total;
  const showBadge = badge > 0;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={onToggle}
        data-testid="notif-bell-button"
        aria-label={`Notifikasi (${badge} baru)`}
        className="relative w-10 h-10 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
      >
        <Bell size={20} weight={showBadge ? "fill" : "regular"} className={showBadge ? "text-primary" : ""} />
        {showBadge && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center leading-none"
            data-testid="notif-bell-badge"
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="fixed md:absolute left-2 right-2 md:left-full md:right-auto md:ml-3 top-16 md:top-0 md:w-[380px] bg-card border border-border shadow-2xl z-50 rounded-xl overflow-hidden"
          data-testid="notif-panel"
        >
          <div className="p-3 border-b border-border flex items-center justify-between bg-secondary/40">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Notifikasi</div>
              <div className="text-sm font-black">
                {items.length === 0 ? "Semua beres" : `${items.length} item aktif`}
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 -mr-1" data-testid="notif-close">
              <X size={16} />
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle size={36} weight="fill" className="text-emerald-500 mx-auto mb-2" />
                <div className="text-sm text-muted-foreground">
                  Tidak ada notifikasi aktif.
                </div>
              </div>
            ) : (
              items.map((it) => {
                const Icon = ICONS[it.kind] || Bell;
                const tone = TONES[it.tone] || TONES.info;
                return (
                  <Link
                    key={it.id}
                    to={it.link}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-3 p-3 hover:bg-secondary/50 border-b border-border/60 last:border-b-0 transition-colors"
                    data-testid={`notif-item-${it.kind}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${tone.bg} ${tone.icon}`}>
                      <Icon size={16} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm">{it.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {it.subtitle}
                      </div>
                    </div>
                    {it.count > 0 && (
                      <div className="text-xs font-mono font-bold text-primary shrink-0">
                        {it.count > 99 ? "99+" : it.count}
                      </div>
                    )}
                  </Link>
                );
              })
            )}
          </div>
          <button
            onClick={() => { markRead(); load(); }}
            className="w-full p-2.5 border-t border-border text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-secondary/60 transition-colors"
            data-testid="notif-refresh"
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
}
